"use client";

import { LazyEncapsulatedQuickAdd } from "@faire/retailer-visitor-shared/components/QuickAdd/LazyEncapsulatedQuickAdd";
import { QuickAddProps } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddProps";
import { QuickAddModalContext } from "@faire/retailer-visitor-shared/features/ProductQuickAddModal/QuickAddModalContext";
import { useProductTileClickImpressions } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/useProductTileClickImpressions";
import { useFaireProtection } from "@faire/retailer-visitor-shared/stores/domain/FaireProtection/useFaireProtection";
import React, { Suspense } from "react";

import { ComponentDisplayOnHoverContainer } from "../../../ComposableProductTile/__internal__/__internal__/ComponentDisplayOnHoverContainer";
import { useProductTileContainerRef } from "../../../ComposableProductTile/__internal__/__internal__/ProductTileContainerRefContextProvider";
import { useCanQuickAdd } from "../../../ComposableProductTile/__internal__/__internal__/useCanQuickAdd";
import { useContainerRef } from "../../../ComposableProductTile/__internal__/__internal__/useContainerRef";

import { useTileInteractions } from "./TileInteractionsContextProvider";

export const QuickAdd: React.FC<{
  data: QuickAddProps & { showOnHover: boolean };
}> = ({ data }) => {
  const { overlayActions } = useTileInteractions();

  const { recordProductTileClickEvent } = useProductTileClickImpressions(
    {
      token: data.productToken,
      brandToken: data.brandToken,
      impressionId: data.impressionId,
      promotedBy: data.promotedBy,
    },
    data.brandToken
  );

  // In the case where the user opens the quick add modal and navigates to the product details page,
  // we want to record a product tile click event
  const onViewProductDetailsClick = () => {
    recordProductTileClickEvent();
  };

  const containerRef = useProductTileContainerRef();
  const currentContainerRef = useContainerRef(containerRef);
  const { isBrandTokenForbidden } = useFaireProtection();
  const isFaireProtectionRestricted = isBrandTokenForbidden(
    data.brandToken ?? ""
  );

  const canQuickAddProduct = useCanQuickAdd(data.state, data.quickAdd);
  if (!canQuickAddProduct) {
    return null;
  }

  const QuickAddWrapper = data.showOnHover
    ? ComponentDisplayOnHoverContainer
    : React.Fragment;

  return (
    <QuickAddModalContext.Provider
      value={{ onViewProductDetailsClick: onViewProductDetailsClick }}
    >
      <QuickAddWrapper>
        <div
          style={{ position: "absolute", right: "0px" }}
          data-test-id="composable-quick-add"
        >
          <Suspense fallback={null} key={`${data.productToken}-suspense`}>
            <LazyEncapsulatedQuickAdd
              key={`${data.productToken}-encapsulated-quick-add`}
              productToken={data.productToken}
              quickAdd={data.quickAdd}
              brandToken={data.brandToken}
              imageContainerRef={
                currentContainerRef
                  ? { current: currentContainerRef }
                  : undefined
              }
              onQuickAddClick={overlayActions?.onQuickAddClick}
              onQuickAddSubmit={overlayActions?.onQuickAddSubmit}
              onChooseOption={overlayActions?.onChooseOptionClick}
              onChooseOptionQuantityUpdate={
                overlayActions?.onChooseOptionQuantityUpdateSubmit
              }
              disableQuickAddWithFaireProtectionTooltip={
                isFaireProtectionRestricted
              }
            />
          </Suspense>
        </div>
      </QuickAddWrapper>
    </QuickAddModalContext.Provider>
  );
};
